import * as Types from '../../tenants/kwg/generated';

import { UserFragmentDoc, UserDetailFragmentDoc } from './users.fragment.g';
import { GroupFragmentDoc } from '../groups/groups.fragment.g';
import { RoleAssignmentFragmentDoc } from '../roleAssigments/roleAssignments.fragment.g';
import { useQuery, useSuspenseQuery, useInfiniteQuery, useSuspenseInfiniteQuery, UseQueryOptions, UseSuspenseQueryOptions, UseInfiniteQueryOptions, InfiniteData, UseSuspenseInfiniteQueryOptions } from '@tanstack/react-query';
import { fetcher } from '../fetcher';


export const UserPicklistDocument = `
    query UserPicklist($order: UserSortInput!, $first: Int, $after: String, $where: UserFilterInput) {
  users(order: [$order], first: $first, after: $after, where: $where) {
    edges {
      node {
        ...User
      }
    }
    totalCount
    pageInfo {
      startCursor
      endCursor
      hasNextPage
      hasPreviousPage
    }
  }
}
    ${UserFragmentDoc}`;

export const useUserPicklistQuery = <
      TData = Types.UserPicklistQuery,
      TError = unknown
    >(
      variables: Types.UserPicklistQueryVariables,
      options?: Omit<UseQueryOptions<Types.UserPicklistQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<Types.UserPicklistQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<Types.UserPicklistQuery, TError, TData>(
      {
    queryKey: ['UserPicklist', variables],
    queryFn: fetcher<Types.UserPicklistQuery, Types.UserPicklistQueryVariables>(UserPicklistDocument, variables),
    ...options
  }
    )};

export const useSuspenseUserPicklistQuery = <
      TData = Types.UserPicklistQuery,
      TError = unknown
    >(
      variables: Types.UserPicklistQueryVariables,
      options?: Omit<UseSuspenseQueryOptions<Types.UserPicklistQuery, TError, TData>, 'queryKey'> & { queryKey?: UseSuspenseQueryOptions<Types.UserPicklistQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useSuspenseQuery<Types.UserPicklistQuery, TError, TData>(
      {
    queryKey: ['UserPicklist', variables],
    queryFn: fetcher<Types.UserPicklistQuery, Types.UserPicklistQueryVariables>(UserPicklistDocument, variables),
    ...options
  }
    )};

export const useInfiniteUserPicklistQuery = <
      TData = InfiniteData<Types.UserPicklistQuery>,
      TError = unknown
    >(
      variables: Types.UserPicklistQueryVariables,
      options: Omit<UseInfiniteQueryOptions<Types.UserPicklistQuery, TError, TData>, 'queryKey'> & { queryKey?: UseInfiniteQueryOptions<Types.UserPicklistQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useInfiniteQuery<Types.UserPicklistQuery, TError, TData>(
      (() => {
    const { queryKey: optionsQueryKey, ...restOptions } = options;
    return {
      queryKey: optionsQueryKey ?? ['UserPicklist.infinite', variables],
      queryFn: (metaData) => fetcher<Types.UserPicklistQuery, Types.UserPicklistQueryVariables>(UserPicklistDocument, {...variables, ...(metaData.pageParam ?? {})})(),
      ...restOptions
    }
  })()
    )};

export const useSuspenseInfiniteUserPicklistQuery = <
      TData = InfiniteData<Types.UserPicklistQuery>,
      TError = unknown
    >(
      variables: Types.UserPicklistQueryVariables,
      options: Omit<UseSuspenseInfiniteQueryOptions<Types.UserPicklistQuery, TError, TData>, 'queryKey'> & { queryKey?: UseSuspenseInfiniteQueryOptions<Types.UserPicklistQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useSuspenseInfiniteQuery<Types.UserPicklistQuery, TError, TData>(
      (() => {
    const { queryKey: optionsQueryKey, ...restOptions } = options;
    return {
      queryKey: optionsQueryKey ?? ['UserPicklist.infinite', variables],
      queryFn: (metaData) => fetcher<Types.UserPicklistQuery, Types.UserPicklistQueryVariables>(UserPicklistDocument, {...variables, ...(metaData.pageParam ?? {})})(),
      ...restOptions
    }
  })()
    )};

export const UsersDocument = `
    query Users($order: [UserSortInput!], $first: Int, $after: String, $where: UserFilterInput) {
  users(order: $order, first: $first, after: $after, where: $where) {
    edges {
      node {
        ...UserDetail
      }
    }
    totalCount
    pageInfo {
      startCursor
      endCursor
      hasNextPage
      hasPreviousPage
    }
  }
}
    ${UserDetailFragmentDoc}
${GroupFragmentDoc}
${RoleAssignmentFragmentDoc}`;

export const useUsersQuery = <
      TData = Types.UsersQuery,
      TError = unknown
    >(
      variables?: Types.UsersQueryVariables,
      options?: Omit<UseQueryOptions<Types.UsersQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<Types.UsersQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<Types.UsersQuery, TError, TData>(
      {
    queryKey: variables === undefined ? ['Users'] : ['Users', variables],
    queryFn: fetcher<Types.UsersQuery, Types.UsersQueryVariables>(UsersDocument, variables),
    ...options
  }
    )};

export const useSuspenseUsersQuery = <
      TData = Types.UsersQuery,
      TError = unknown
    >(
      variables?: Types.UsersQueryVariables,
      options?: Omit<UseSuspenseQueryOptions<Types.UsersQuery, TError, TData>, 'queryKey'> & { queryKey?: UseSuspenseQueryOptions<Types.UsersQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useSuspenseQuery<Types.UsersQuery, TError, TData>(
      {
    queryKey: variables === undefined ? ['Users'] : ['Users', variables],
    queryFn: fetcher<Types.UsersQuery, Types.UsersQueryVariables>(UsersDocument, variables),
    ...options
  }
    )};

export const useInfiniteUsersQuery = <
      TData = InfiniteData<Types.UsersQuery>,
      TError = unknown
    >(
      variables: Types.UsersQueryVariables,
      options: Omit<UseInfiniteQueryOptions<Types.UsersQuery, TError, TData>, 'queryKey'> & { queryKey?: UseInfiniteQueryOptions<Types.UsersQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useInfiniteQuery<Types.UsersQuery, TError, TData>(
      (() => {
    const { queryKey: optionsQueryKey, ...restOptions } = options;
    return {
      queryKey: optionsQueryKey ?? variables === undefined ? ['Users.infinite'] : ['Users.infinite', variables],
      queryFn: (metaData) => fetcher<Types.UsersQuery, Types.UsersQueryVariables>(UsersDocument, {...variables, ...(metaData.pageParam ?? {})})(),
      ...restOptions
    }
  })()
    )};

export const useSuspenseInfiniteUsersQuery = <
      TData = InfiniteData<Types.UsersQuery>,
      TError = unknown
    >(
      variables: Types.UsersQueryVariables,
      options: Omit<UseSuspenseInfiniteQueryOptions<Types.UsersQuery, TError, TData>, 'queryKey'> & { queryKey?: UseSuspenseInfiniteQueryOptions<Types.UsersQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useSuspenseInfiniteQuery<Types.UsersQuery, TError, TData>(
      (() => {
    const { queryKey: optionsQueryKey, ...restOptions } = options;
    return {
      queryKey: optionsQueryKey ?? variables === undefined ? ['Users.infinite'] : ['Users.infinite', variables],
      queryFn: (metaData) => fetcher<Types.UsersQuery, Types.UsersQueryVariables>(UsersDocument, {...variables, ...(metaData.pageParam ?? {})})(),
      ...restOptions
    }
  })()
    )};

export const AllUsersDocument = `
    query AllUsers($order: [UserSortInput!], $first: Int, $after: String, $where: UserFilterInput) {
  allUsers(order: $order, first: $first, after: $after, where: $where) {
    edges {
      node {
        ...UserDetail
      }
    }
    totalCount
    pageInfo {
      startCursor
      endCursor
      hasNextPage
      hasPreviousPage
    }
  }
}
    ${UserDetailFragmentDoc}
${GroupFragmentDoc}
${RoleAssignmentFragmentDoc}`;

export const useAllUsersQuery = <
      TData = Types.AllUsersQuery,
      TError = unknown
    >(
      variables?: Types.AllUsersQueryVariables,
      options?: Omit<UseQueryOptions<Types.AllUsersQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<Types.AllUsersQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<Types.AllUsersQuery, TError, TData>(
      {
    queryKey: variables === undefined ? ['AllUsers'] : ['AllUsers', variables],
    queryFn: fetcher<Types.AllUsersQuery, Types.AllUsersQueryVariables>(AllUsersDocument, variables),
    ...options
  }
    )};

export const useSuspenseAllUsersQuery = <
      TData = Types.AllUsersQuery,
      TError = unknown
    >(
      variables?: Types.AllUsersQueryVariables,
      options?: Omit<UseSuspenseQueryOptions<Types.AllUsersQuery, TError, TData>, 'queryKey'> & { queryKey?: UseSuspenseQueryOptions<Types.AllUsersQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useSuspenseQuery<Types.AllUsersQuery, TError, TData>(
      {
    queryKey: variables === undefined ? ['AllUsers'] : ['AllUsers', variables],
    queryFn: fetcher<Types.AllUsersQuery, Types.AllUsersQueryVariables>(AllUsersDocument, variables),
    ...options
  }
    )};

export const useInfiniteAllUsersQuery = <
      TData = InfiniteData<Types.AllUsersQuery>,
      TError = unknown
    >(
      variables: Types.AllUsersQueryVariables,
      options: Omit<UseInfiniteQueryOptions<Types.AllUsersQuery, TError, TData>, 'queryKey'> & { queryKey?: UseInfiniteQueryOptions<Types.AllUsersQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useInfiniteQuery<Types.AllUsersQuery, TError, TData>(
      (() => {
    const { queryKey: optionsQueryKey, ...restOptions } = options;
    return {
      queryKey: optionsQueryKey ?? variables === undefined ? ['AllUsers.infinite'] : ['AllUsers.infinite', variables],
      queryFn: (metaData) => fetcher<Types.AllUsersQuery, Types.AllUsersQueryVariables>(AllUsersDocument, {...variables, ...(metaData.pageParam ?? {})})(),
      ...restOptions
    }
  })()
    )};

export const useSuspenseInfiniteAllUsersQuery = <
      TData = InfiniteData<Types.AllUsersQuery>,
      TError = unknown
    >(
      variables: Types.AllUsersQueryVariables,
      options: Omit<UseSuspenseInfiniteQueryOptions<Types.AllUsersQuery, TError, TData>, 'queryKey'> & { queryKey?: UseSuspenseInfiniteQueryOptions<Types.AllUsersQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useSuspenseInfiniteQuery<Types.AllUsersQuery, TError, TData>(
      (() => {
    const { queryKey: optionsQueryKey, ...restOptions } = options;
    return {
      queryKey: optionsQueryKey ?? variables === undefined ? ['AllUsers.infinite'] : ['AllUsers.infinite', variables],
      queryFn: (metaData) => fetcher<Types.AllUsersQuery, Types.AllUsersQueryVariables>(AllUsersDocument, {...variables, ...(metaData.pageParam ?? {})})(),
      ...restOptions
    }
  })()
    )};

export const UserByIdDocument = `
    query UserById($id: UUID!) {
  userById(id: $id) {
    ...UserDetail
  }
}
    ${UserDetailFragmentDoc}
${GroupFragmentDoc}
${RoleAssignmentFragmentDoc}`;

export const useUserByIdQuery = <
      TData = Types.UserByIdQuery,
      TError = unknown
    >(
      variables: Types.UserByIdQueryVariables,
      options?: Omit<UseQueryOptions<Types.UserByIdQuery, TError, TData>, 'queryKey'> & { queryKey?: UseQueryOptions<Types.UserByIdQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useQuery<Types.UserByIdQuery, TError, TData>(
      {
    queryKey: ['UserById', variables],
    queryFn: fetcher<Types.UserByIdQuery, Types.UserByIdQueryVariables>(UserByIdDocument, variables),
    ...options
  }
    )};

export const useSuspenseUserByIdQuery = <
      TData = Types.UserByIdQuery,
      TError = unknown
    >(
      variables: Types.UserByIdQueryVariables,
      options?: Omit<UseSuspenseQueryOptions<Types.UserByIdQuery, TError, TData>, 'queryKey'> & { queryKey?: UseSuspenseQueryOptions<Types.UserByIdQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useSuspenseQuery<Types.UserByIdQuery, TError, TData>(
      {
    queryKey: ['UserById', variables],
    queryFn: fetcher<Types.UserByIdQuery, Types.UserByIdQueryVariables>(UserByIdDocument, variables),
    ...options
  }
    )};

export const useInfiniteUserByIdQuery = <
      TData = InfiniteData<Types.UserByIdQuery>,
      TError = unknown
    >(
      variables: Types.UserByIdQueryVariables,
      options: Omit<UseInfiniteQueryOptions<Types.UserByIdQuery, TError, TData>, 'queryKey'> & { queryKey?: UseInfiniteQueryOptions<Types.UserByIdQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useInfiniteQuery<Types.UserByIdQuery, TError, TData>(
      (() => {
    const { queryKey: optionsQueryKey, ...restOptions } = options;
    return {
      queryKey: optionsQueryKey ?? ['UserById.infinite', variables],
      queryFn: (metaData) => fetcher<Types.UserByIdQuery, Types.UserByIdQueryVariables>(UserByIdDocument, {...variables, ...(metaData.pageParam ?? {})})(),
      ...restOptions
    }
  })()
    )};

export const useSuspenseInfiniteUserByIdQuery = <
      TData = InfiniteData<Types.UserByIdQuery>,
      TError = unknown
    >(
      variables: Types.UserByIdQueryVariables,
      options: Omit<UseSuspenseInfiniteQueryOptions<Types.UserByIdQuery, TError, TData>, 'queryKey'> & { queryKey?: UseSuspenseInfiniteQueryOptions<Types.UserByIdQuery, TError, TData>['queryKey'] }
    ) => {
    
    return useSuspenseInfiniteQuery<Types.UserByIdQuery, TError, TData>(
      (() => {
    const { queryKey: optionsQueryKey, ...restOptions } = options;
    return {
      queryKey: optionsQueryKey ?? ['UserById.infinite', variables],
      queryFn: (metaData) => fetcher<Types.UserByIdQuery, Types.UserByIdQueryVariables>(UserByIdDocument, {...variables, ...(metaData.pageParam ?? {})})(),
      ...restOptions
    }
  })()
    )};
